import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { toast } from 'sonner';
import { actionLogger } from './actionLogger';

export type NotificationType = 'email' | 'sms' | 'in_app' | 'push';

export type NotificationTrigger =
  | 'transaction_created'
  | 'transaction_updated'
  | 'transaction_deleted'
  | 'payment_due'
  | 'payment_overdue'
  | 'low_balance'
  | 'budget_exceeded'
  | 'system_error'
  | 'backup_completed'
  | 'undo_action'
  | 'redo_action';

export interface NotificationPreference {
  type: NotificationType;
  enabled: boolean;
  triggers: NotificationTrigger[];
  frequency: 'immediate' | 'hourly' | 'daily' | 'weekly';
  quietHours: {
    enabled: boolean;
    start: string; // HH:MM format
    end: string;   // HH:MM format
  };
  customSettings?: Record<string, any>;
}

export interface InAppNotification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  timestamp: number;
  read: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
  metadata?: {
    trigger?: NotificationTrigger;
    transactionId?: string;
    correlationId?: string;
  };
  autoHide?: boolean;
  duration?: number;
}

export interface EmailNotificationPayload {
  to: string[];
  subject: string;
  body: string;
  htmlBody?: string;
  attachments?: Array<{
    filename: string;
    content: string | Buffer;
    contentType: string;
  }>;
  metadata?: {
    template?: string;
    variables?: Record<string, any>;
    priority?: 'low' | 'normal' | 'high';
  };
}

export interface SMSNotificationPayload {
  to: string[];
  message: string;
  metadata?: {
    template?: string;
    variables?: Record<string, any>;
    priority?: 'low' | 'normal' | 'high';
  };
}

export interface NotificationState {
  // Preferences
  preferences: NotificationType[];
  inAppNotifications: InAppNotification[];

  // Settings
  email: string;
  phone: string;
  globalEnabled: boolean;

  // Actions
  updatePreferences: (preferences: NotificationType[]) => void;
  addInAppNotification: (notification: Omit<InAppNotification, 'id' | 'timestamp' | 'read'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearNotifications: () => void;
  sendEmail: (payload: EmailNotificationPayload) => Promise<void>;
  sendSMS: (payload: SMSNotificationPayload) => Promise<void>;
  triggerNotification: (
    trigger: NotificationTrigger,
    data: any,
    types?: NotificationType[]
  ) => Promise<void>;
  updateSettings: (settings: Partial<NotificationState>) => void;

  // Getters
  getUnreadCount: () => number;
  getPreferencesByType: (type: NotificationType) => NotificationPreference | undefined;
  canSendNotification: (type: NotificationType) => boolean;
}

const defaultPreferences: NotificationPreference[] = [
  {
    type: 'email',
    enabled: true,
    triggers: ['transaction_created', 'transaction_updated', 'payment_due', 'payment_overdue'],
    frequency: 'immediate',
    quietHours: {
      enabled: true,
      start: '22:00',
      end: '07:00',
    },
  },
  {
    type: 'sms',
    enabled: false,
    triggers: ['payment_overdue', 'system_error'],
    frequency: 'immediate',
    quietHours: {
      enabled: true,
      start: '22:00',
      end: '07:00',
    },
  },
  {
    type: 'in_app',
    enabled: true,
    triggers: ['transaction_created', 'transaction_updated', 'transaction_deleted', 'undo_action', 'redo_action'],
    frequency: 'immediate',
    quietHours: {
      enabled: false,
      start: '22:00',
      end: '07:00',
    },
  },
];

function generateNotificationId(): string {
  return `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

function isInQuietHours(preferences: NotificationPreference): boolean {
  if (!preferences.quietHours.enabled) return false;

  const now = new Date();
  const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

  return currentTime >= preferences.quietHours.start && currentTime <= preferences.quietHours.end;
}

class NotificationService {
  // Email templates
  private emailTemplates = {
    transaction_created: {
      subject: 'Nova Transação Criada',
      template: 'transaction-created',
    },
    transaction_updated: {
      subject: 'Transação Atualizada',
      template: 'transaction-updated',
    },
    transaction_deleted: {
      subject: 'Transação Excluída',
      template: 'transaction-deleted',
    },
    payment_due: {
      subject: 'Pagamento Pendente',
      template: 'payment-due',
    },
    payment_overdue: {
      subject: 'Pagamento Atrasado',
      template: 'payment-overdue',
    },
    system_error: {
      subject: 'Erro no Sistema',
      template: 'system-error',
    },
  };

  // SMS templates
  private smsTemplates = {
    payment_overdue: (data: any) => `⚠️ Pagamento atrasado: ${data.description} - R$ ${data.amount}`,
    system_error: (data: any) => `🚨 Erro no sistema: ${data.message}`,
    low_balance: (data: any) => `💰 Saldo baixo: R$ ${data.balance}`,
  };

  async sendEmail(payload: EmailNotificationPayload): Promise<void> {
    try {
      actionLogger.logNotification('email', 'send', payload.subject, payload);

      // Simulate API call - replace with actual email service
      await this.simulateEmailAPI(payload);

      actionLogger.success('Email Sent', `Email sent to ${payload.to.join(', ')}`, {
        category: 'notification',
        details: {
          recipients: payload.to,
          subject: payload.subject,
          template: payload.metadata?.template,
        },
      });

    } catch (error) {
      actionLogger.logNotification('email', 'send_error', payload.subject, error);
      throw error;
    }
  }

  async sendSMS(payload: SMSNotificationPayload): Promise<void> {
    try {
      actionLogger.logNotification('sms', 'send', payload.message, payload);

      // Simulate API call - replace with actual SMS service
      await this.simulateSMSAPI(payload);

      actionLogger.success('SMS Sent', `SMS sent to ${payload.to.join(', ')}`, {
        category: 'notification',
        details: {
          recipients: payload.to,
          message: payload.message,
          template: payload.metadata?.template,
        },
      });

    } catch (error) {
      actionLogger.logNotification('sms', 'send_error', payload.message, error);
      throw error;
    }
  }

  private async simulateEmailAPI(payload: EmailNotificationPayload): Promise<void> {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Simulate success (in production, this would be an actual API call)
    console.log('Email API call:', payload);
  }

  private async simulateSMSAPI(payload: SMSNotificationPayload): Promise<void> {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 500));

    // Simulate success (in production, this would be an actual API call)
    console.log('SMS API call:', payload);
  }

  async triggerNotification(
    trigger: NotificationTrigger,
    data: any,
    types?: NotificationType[]
  ): Promise<void> {
    const state = useNotificationService.getState();
    const notificationTypes = types || ['email', 'sms', 'in_app'];

    for (const type of notificationTypes) {
      const preference = state.getPreferencesByType(type);

      if (!preference?.enabled || !state.canSendNotification(type)) {
        continue;
      }

      if (!preference.triggers.includes(trigger)) {
        continue;
      }

      if (isInQuietHours(preference)) {
        actionLogger.info('Notification Skipped', `${type} notification skipped due to quiet hours`, {
          category: 'notification',
          details: {
            type,
            trigger,
            quietHours: preference.quietHours,
          },
        });
        continue;
      }

      try {
        await this.processNotification(type, trigger, data, preference);
      } catch (error) {
        actionLogger.error('Notification Failed', `Failed to send ${type} notification for ${trigger}`, {
          category: 'error',
          details: {
            type,
            trigger,
            error: error?.message || String(error),
          },
        });
      }
    }
  }

  private async processNotification(
    type: NotificationType,
    trigger: NotificationTrigger,
    data: any,
    preference: NotificationPreference
  ): Promise<void> {
    switch (type) {
      case 'email':
        await this.processEmailNotification(trigger, data);
        break;
      case 'sms':
        await this.processSMSNotification(trigger, data);
        break;
      case 'in_app':
        await this.processInAppNotification(trigger, data);
        break;
    }
  }

  private async processEmailNotification(trigger: NotificationTrigger, data: any): Promise<void> {
    const template = this.emailTemplates[trigger as keyof typeof this.emailTemplates];
    if (!template) return;

    const state = useNotificationService.getState();
    const payload: EmailNotificationPayload = {
      to: [state.email],
      subject: template.subject,
      body: this.generateEmailBody(template.template, data),
      metadata: {
        template: template.template,
        variables: data,
        priority: this.getNotificationPriority(trigger),
      },
    };

    await this.sendEmail(payload);
  }

  private async processSMSNotification(trigger: NotificationTrigger, data: any): Promise<void> {
    const templateGenerator = this.smsTemplates[trigger as keyof typeof this.smsTemplates];
    if (!templateGenerator) return;

    const state = useNotificationService.getState();
    const payload: SMSNotificationPayload = {
      to: [state.phone],
      message: templateGenerator(data),
      metadata: {
        template: trigger,
        variables: data,
        priority: this.getNotificationPriority(trigger),
      },
    };

    await this.sendSMS(payload);
  }

  private async processInAppNotification(trigger: NotificationTrigger, data: any): Promise<void> {
    const notification = this.createInAppNotification(trigger, data);
    const state = useNotificationService.getState();

    state.addInAppNotification(notification);

    // Show toast notification
    this.showToastNotification(notification);
  }

  private createInAppNotification(trigger: NotificationTrigger, data: any): Omit<InAppNotification, 'id' | 'timestamp' | 'read'> {
    const notifications = {
      transaction_created: {
        type: 'success' as const,
        title: 'Transação Criada',
        message: `Transação "${data.description}" foi criada com sucesso.`,
      },
      transaction_updated: {
        type: 'info' as const,
        title: 'Transação Atualizada',
        message: `Transação "${data.description}" foi atualizada.`,
      },
      transaction_deleted: {
        type: 'warning' as const,
        title: 'Transação Excluída',
        message: `Transação "${data.description}" foi excluída.`,
      },
      undo_action: {
        type: 'info' as const,
        title: 'Ação Desfeita',
        message: 'A última ação foi desfeita com sucesso.',
      },
      redo_action: {
        type: 'info' as const,
        title: 'Ação Revezida',
        message: 'A ação desfeita foi aplicada novamente.',
      },
      payment_due: {
        type: 'warning' as const,
        title: 'Pagamento Pendente',
        message: `Pagamento de "${data.description}" vence em breve.`,
      },
      payment_overdue: {
        type: 'error' as const,
        title: 'Pagamento Atrasado',
        message: `Pagamento de "${data.description}" está atrasado.`,
      },
      system_error: {
        type: 'error' as const,
        title: 'Erro no Sistema',
        message: 'Ocorreu um erro inesperado. Tente novamente.',
      },
    };

    const notification = notifications[trigger] || {
      type: 'info' as const,
      title: 'Notificação',
      message: 'Nova notificação recebida.',
    };

    return {
      ...notification,
      metadata: {
        trigger,
        transactionId: data.id || data.transactionId,
        correlationId: data.correlationId,
      },
      autoHide: true,
      duration: 5000,
    };
  }

  private showToastNotification(notification: Omit<InAppNotification, 'id' | 'timestamp' | 'read'>) {
    switch (notification.type) {
      case 'success':
        toast.success(notification.title, {
          description: notification.message,
          duration: notification.duration,
        });
        break;
      case 'error':
        toast.error(notification.title, {
          description: notification.message,
          duration: notification.duration,
        });
        break;
      case 'warning':
        toast.warning(notification.title, {
          description: notification.message,
          duration: notification.duration,
        });
        break;
      case 'info':
        toast.info(notification.title, {
          description: notification.message,
          duration: notification.duration,
        });
        break;
    }
  }

  private generateEmailBody(template: string, data: any): string {
    // Helper function to safely format amount
    const formatAmount = (amount: any) => {
      const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
      return !isNaN(numAmount) && numAmount !== null && numAmount !== undefined 
        ? numAmount.toFixed(2) 
        : '0,00';
    };

    // Simple template generation - in production, use a proper template engine
    const templates = {
      'transaction-created': `
        Nova transação criada:

        Descrição: ${data.description}
        Valor: R$ ${formatAmount(data.amount)}
        Tipo: ${data.type === 'income' ? 'Receita' : 'Despesa'}
        Data: ${new Date(data.date).toLocaleDateString('pt-BR')}

        Verifique no aplicativo para mais detalhes.
      `,
      'transaction-updated': `
        Transação atualizada:

        Descrição: ${data.description}
        Valor: R$ ${formatAmount(data.amount)}
        Tipo: ${data.type === 'income' ? 'Receita' : 'Despesa'}

        Verifique no aplicativo para mais detalhes.
      `,
      'payment-due': `
        Pagamento pendente:

        Descrição: ${data.description}
        Valor: R$ ${formatAmount(data.amount)}
        Data de vencimento: ${new Date(data.dueDate).toLocaleDateString('pt-BR')}

        Não se esqueça de pagar até a data de vencimento.
      `,
    };

    return templates[template as keyof typeof templates] || `
      Notificação do sistema:

      ${JSON.stringify(data, null, 2)}
    `;
  }

  private getNotificationPriority(trigger: NotificationTrigger): 'low' | 'normal' | 'high' {
    const highPriority = ['payment_overdue', 'system_error'];
    const lowPriority = ['backup_completed'];

    if (highPriority.includes(trigger)) return 'high';
    if (lowPriority.includes(trigger)) return 'low';
    return 'normal';
  }
}

export const useNotificationService = create<NotificationState>()(
  persist(
    (set, get) => ({
      // Initial state
      preferences: defaultPreferences,
      inAppNotifications: [],
      email: '',
      phone: '',
      globalEnabled: true,

      // Update preferences
      updatePreferences: (preferences) => {
        set({ preferences });

        actionLogger.info('Preferences Updated', 'Notification preferences updated', {
          category: 'user_action',
          details: {
            preferencesCount: preferences.length,
          },
        });
      },

      // Add in-app notification
      addInAppNotification: (notification) => {
        const newNotification: InAppNotification = {
          ...notification,
          id: generateNotificationId(),
          timestamp: Date.now(),
          read: false,
        };

        set((state) => ({
          inAppNotifications: [newNotification, ...state.inAppNotifications.slice(0, 100)], // Keep last 100
        }));

        actionLogger.info('In-App Notification Added', newNotification.message, {
          category: 'notification',
          details: {
            type: newNotification.type,
            title: newNotification.title,
            trigger: newNotification.metadata?.trigger,
          },
        });
      },

      // Mark as read
      markAsRead: (id) => {
        set((state) => ({
          inAppNotifications: state.inAppNotifications.map((notif) =>
            notif.id === id ? { ...notif, read: true } : notif
          ),
        }));
      },

      // Mark all as read
      markAllAsRead: () => {
        set((state) => ({
          inAppNotifications: state.inAppNotifications.map((notif) => ({ ...notif, read: true })),
        }));
      },

      // Clear notifications
      clearNotifications: () => {
        set({ inAppNotifications: [] });

        actionLogger.info('Notifications Cleared', 'All in-app notifications cleared', {
          category: 'user_action',
        });
      },

      // Send email
      sendEmail: async (payload) => {
        await new NotificationService().sendEmail(payload);
      },

      // Send SMS
      sendSMS: async (payload) => {
        await new NotificationService().sendSMS(payload);
      },

      // Trigger notification
      triggerNotification: async (trigger, data, types) => {
        await new NotificationService().triggerNotification(trigger, data, types);
      },

      // Update settings
      updateSettings: (settings) => {
        set((state) => ({
          ...state,
          ...settings,
        }));

        actionLogger.info('Settings Updated', 'Notification settings updated', {
          category: 'user_action',
          details: {
            updatedFields: Object.keys(settings),
          },
        });
      },

      // Get unread count
      getUnreadCount: () => {
        return get().inAppNotifications.filter((notif) => !notif.read).length;
      },

      // Get preferences by type
      getPreferencesByType: (type) => {
        return get().preferences.find((pref) => pref.type === type);
      },

      // Check if can send notification
      canSendNotification: (type) => {
        const state = get();
        if (!state.globalEnabled) return false;

        const preference = state.getPreferencesByType(type);
        if (!preference?.enabled) return false;

        if (type === 'email' && !state.email) return false;
        if (type === 'sms' && !state.phone) return false;

        return true;
      },
    }),
    {
      name: 'notification-service-storage',
      partialize: (state) => ({
        preferences: state.preferences,
        email: state.email,
        phone: state.phone,
        globalEnabled: state.globalEnabled,
      }),
    }
  )
);

// Export notification service instance
export const notificationService = new NotificationService();

// Hook for easier usage
export function useNotifications() {
  const state = useNotificationService();

  return {
    // State
    preferences: state.preferences,
    notifications: state.inAppNotifications,
    email: state.email,
    phone: state.phone,
    globalEnabled: state.globalEnabled,
    unreadCount: state.getUnreadCount(),

    // Actions
    updatePreferences: state.updatePreferences,
    addNotification: state.addInAppNotification,
    markAsRead: state.markAsRead,
    markAllAsRead: state.markAllAsRead,
    clearNotifications: state.clearNotifications,
    sendEmail: state.sendEmail,
    sendSMS: state.sendSMS,
    triggerNotification: state.triggerNotification,
    updateSettings: state.updateSettings,
    canSend: state.canSendNotification,
    getPreferencesByType: state.getPreferencesByType,
  };
}